import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { CSSTransition } from 'react-transition-group'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterBreadcrumbsMedChronAsset from '@/matter/components/matter-breadcrumbs/MatterBreadcrumbsMedChronAsset'
import ErrorComponent from '@/components/error/error-component'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { useHubspotConversations } from '@/context/useHubspotConversations'
import { useViewportSizeInPixels } from '@/hooks/use-viewport-size-in-pixels'
import ContentViewerColumn from '@/content-viewer/components/ContentViewerColumn'
import ContentViewerSlideover from '@/content-viewer/components/ContentViewerSlideover'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { selectIsContentViewerOpen } from '@/content-viewer/store/selectors'
import { DEVICE_TYPE_BREAKPOINTS } from '@/hooks/useDeviceType'
import { CONTENT_VIEWER_COLUMN_TRANSITION_DURATION_MS } from '@/medical-common/constants'
import { AddDocuments } from '@/assets/icons'
import { matterPaths } from '@/routing/paths'
import { formatLastActivityForCell } from '@/matter/utils/format-last-activity'
import { MatterAssetFeatureProvider } from '@/matter-asset-common/feature/MatterAssetFeatureProvider'
import { getMedChronAssetTabName, MedChronAssetTabs } from '../constants'
import { medChronFeatureConfig } from '../MedChronFeatureConfig'
import { MedChronAssetActions } from '../store/slice'
import { selectIsUpdateActive, selectMedChronAssetError, selectMedChronAssetForMatter, selectUpdateStatusForAsset } from '../store/selectors'
import { MedChronUpdateStatus } from '../store/schemas'
import MedChronAssetEmptyState from './MedChronAssetEmptyState'
import MedChronAssetBanners from './MedChronAssetBanners'
import MedChronAssetProcessingSkeleton from './MedChronAssetProcessingSkeleton'
import { MedChronVersionHistoryPopover } from './version-history/MedChronVersionHistoryPopover'

const TABS = [
  { name: getMedChronAssetTabName(MedChronAssetTabs.ALL_EVENTS), path: MedChronAssetTabs.ALL_EVENTS, id: 'all_events_tab' },
  { name: getMedChronAssetTabName(MedChronAssetTabs.DETAILED_VIEW), path: MedChronAssetTabs.DETAILED_VIEW, id: 'detailed_view_tab' },
  { name: getMedChronAssetTabName(MedChronAssetTabs.SOURCE_FILES), path: MedChronAssetTabs.SOURCE_FILES, id: 'source_files_tab' },
]

/**
 * Layout for the matter's living Medical Chronology.
 *
 * Branches on the asset state: loading, error, empty (no chronology yet), first build in
 * flight (skeleton), or the tabbed chronology with status banners and the content viewer
 * split (column on desktop, slideover on mobile).
 */
export default function MedChronAssetLayout() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { toggleWidget } = useHubspotConversations()
  // Lifted so the failed-files banner's "Version info" action can open the version-history panel.
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false)

  const { width: viewportWidth } = useViewportSizeInPixels()
  const useColumnLayout = viewportWidth >= DEVICE_TYPE_BREAKPOINTS.DESKTOP

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const matterId = activeMatterId ?? undefined
  const asset = useAppSelector((state: RootState) => selectMedChronAssetForMatter(state, { matterId }))
  const assetError = useAppSelector((state: RootState) => selectMedChronAssetError(state, { matterId }))
  const isUpdateActive = useAppSelector((state: RootState) => selectIsUpdateActive(state, { matterId }))
  const updateStatus = useAppSelector((state: RootState) => selectUpdateStatusForAsset(state, { assetId: asset?.id }))
  const showContentViewer = useAppSelector((state: RootState) => selectIsContentViewerOpen(state))

  const referenceColumnRef = useRef<HTMLDivElement>(null)

  // The first build has no live events yet - show the skeleton until it publishes
  const isFirstBuildInFlight = Boolean(asset && asset.chronology_version === 0 && isUpdateActive)
  // The first build errored with nothing published - a full error state replaces the empty table
  const isFirstBuildError = Boolean(asset && asset.chronology_version === 0 && !isUpdateActive && updateStatus?.update.status === MedChronUpdateStatus.ERROR)

  // Clear content viewer when component unmounts
  useEffect(() => {
    return () => {
      dispatch(ContentViewerActions.clearContentViewer())
    }
  }, [dispatch])

  return (
    <div className="grow flex flex-col w-full h-full overflow-hidden">
      <MatterBreadcrumbsMedChronAsset />

      {/* Loading the overview for the first time */}
      {asset === undefined && !assetError && (
        <div className="flex-1 flex items-center justify-center">
          <CircularProgressContinuousSized size={20} thickness={7} />
        </div>
      )}

      {/* Overview failed to load */}
      {asset === undefined && assetError && (
        <ErrorComponent
          code="500"
          title="Error Loading Medical Chronology"
          message={
            <>
              {'Our servers ran into a problem. Please '}
              <button
                onClick={() => matterId && dispatch(MedChronAssetActions.fetchAssetForMatter({ matterId }))}
                className="text-brand-500 hover:text-brand-400 underline cursor-pointer"
              >
                try again
              </button>
              {', or '}
              <button onClick={toggleWidget} className="text-brand-500 hover:text-brand-400 underline cursor-pointer">
                contact support
              </button>
              {' if the issue continues.'}
            </>
          }
        />
      )}

      {/* No chronology yet */}
      {asset === null && matterId && <MedChronAssetEmptyState matterId={matterId} />}

      {/* The living chronology */}
      {asset && matterId && (
        <MatterAssetFeatureProvider config={medChronFeatureConfig} storeKey={asset.id}>
          <div className="flex flex-1 overflow-hidden">
            {/* Left: banners + tabbed content area */}
            <div
              className={clsx(
                'flex flex-col max-w-none pt-2 lg:pt-3.5 lg:pl-2 transition-[width] duration-200 ease-out',
                showContentViewer && useColumnLayout ? 'w-1/2' : 'w-full'
              )}
            >
              <MedChronAssetBanners matterId={matterId} assetId={asset.id} onShowVersionInfo={() => setVersionHistoryOpen(true)} />

              <div className="grow-0 shrink-0 items-end flex relative gap-1 pl-1 lg:pl-0 z-10 -mb-px">
                {/* Tabs scroll horizontally on narrow screens so the pinned actions stay reachable */}
                <div className="flex-1 min-w-0 flex items-end gap-1 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                  {TABS.map((tab) => (
                    <NavLink
                      key={tab.path}
                      to={tab.path}
                      id={tab.id}
                      className={({ isActive }) =>
                        `relative px-3 py-1 flex items-center gap-1 text-sm font-semibold tracking-[-0.15px] transition-all duration-150 ease-in-out rounded-t-md border whitespace-nowrap ${
                          isActive
                            ? 'bg-brand-neutral-50 text-brand-800 border-b-transparent'
                            : 'bg-brand-neutral-100 text-brand-neutral-600 hover:bg-brand-neutral-200'
                        }`
                      }
                    >
                      {tab.name}
                    </NavLink>
                  ))}
                </div>

                {/* Right-side actions — pinned so they stay visible/reachable even when tabs overflow */}
                <div className="shrink-0 flex items-center gap-3 pl-2 pr-2 pb-1">
                  {asset.last_built_at && (
                    <>
                      <span className="hidden sm:inline text-xs font-medium text-brand-neutral-600 whitespace-nowrap">
                        {formatLastActivityForCell(asset.last_built_at)}
                      </span>
                      <span className="hidden sm:block h-6 w-px bg-brand-neutral-200" aria-hidden />
                    </>
                  )}
                  <button
                    onClick={() => navigate(matterPaths.chronology.addFiles(matterId))}
                    disabled={isUpdateActive}
                    className="inline-flex items-center gap-1 text-sm font-semibold tracking-[-0.15px] px-3 py-1 rounded-md bg-brand-neutral-200 border border-brand-neutral-300 text-brand-neutral-950 hover:bg-brand-neutral-300 disabled:opacity-60 disabled:cursor-not-allowed"
                    title={isUpdateActive ? 'An update is already in progress' : undefined}
                  >
                    <AddDocuments className="w-4 h-4" />
                    Add Files
                  </button>
                  <MedChronVersionHistoryPopover assetId={asset.id} open={versionHistoryOpen} onOpenChange={setVersionHistoryOpen} />
                </div>
              </div>

              <div className="overflow-hidden flex-1 min-h-0 flex flex-col lg:border-l border-t bg-brand-neutral-50">
                <div className="flex flex-col flex-1 h-full">
                  {isFirstBuildInFlight ? (
                    <MedChronAssetProcessingSkeleton />
                  ) : isFirstBuildError && updateStatus ? (
                    <ErrorComponent
                      code="500"
                      title="Medical Chronology Failed to Generate"
                      message={
                        <>
                          {'Something went wrong while building the chronology. Error ID: '}
                          <span className="font-mono">{updateStatus.update.id}</span>
                          {'. Please try again, or '}
                          <button onClick={toggleWidget} className="text-brand-500 hover:text-brand-400 underline cursor-pointer">
                            contact support
                          </button>
                          {' with this ID if the issue continues.'}
                        </>
                      }
                      customLink={{ label: 'Try Again', to: matterPaths.chronology.addFiles(matterId) }}
                    />
                  ) : (
                    <Outlet />
                  )}
                </div>
              </div>
            </div>

            {/* Right: Content Viewer column */}
            <CSSTransition
              appear
              nodeRef={referenceColumnRef}
              in={showContentViewer}
              timeout={{ enter: CONTENT_VIEWER_COLUMN_TRANSITION_DURATION_MS, exit: 0 }}
              classNames="content-viewer-column"
              unmountOnExit
            >
              <div
                ref={referenceColumnRef}
                className={clsx('overflow-hidden flex flex-col h-full transform-gpu will-change-transform', useColumnLayout ? 'w-1/2' : 'w-0')}
              >
                <ContentViewerColumn conversationId="none" />
              </div>
            </CSSTransition>

            {/* Content viewer as slideover - small screens */}
            {!useColumnLayout && <ContentViewerSlideover open={showContentViewer} conversationId="none" />}
          </div>
        </MatterAssetFeatureProvider>
      )}
    </div>
  )
}
